import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import GroupsLayout from "../components/groups/GroupsLayout"
import GroupList from "../components/groups/GroupList"
import GroupInvitesPanel from "../components/groups/GroupInvitesPanel"
import GroupDetails from "../components/groups/GroupDetails"
import GroupCreationWizard from "../components/groups/GroupCreationWizard"
import GroupInviteModal from "../components/groups/GroupInviteModal"
import MonthlySummaryPanel from "../components/MonthlySummaryPanel"
import { SkeletonList, SkeletonStatGrid, SkeletonTable } from "../components/Skeleton"
import api from "../utils/api"
import { getGroupScopedImpersonation } from "../utils/impersonation"

const normalizeGroupId = (groupId) => String(groupId || "").trim()

const normalizeGroupRecord = (group) => {
  if (!group || typeof group !== "object") return null
  const id = normalizeGroupId(group.id || group._id)
  if (!id) return null
  return { ...group, id }
}

const toGroupTabShortName = (groupName) => {
  const normalized = String(groupName || "").trim()
  if (!normalized) return "Group"
  if (normalized.length <= 16) return normalized
  return `${normalized.slice(0, 15)}…`
}

const resolveCreatedGroupFromResponse = (response) => {
  const data = response?.data
  const direct = normalizeGroupRecord(data?.group)
  if (direct) return direct
  const nested = normalizeGroupRecord(data?.data?.group)
  if (nested) return nested
  const payload = normalizeGroupRecord(data?.payload?.group)
  if (payload) return payload

  if (Array.isArray(data?.groups) && data.groups.length === 1) {
    return normalizeGroupRecord(data.groups[0])
  }

  return null
}

function GroupsModule() {
  const location = useLocation()
  const navigate = useNavigate()
  const shouldOpenCreate = !!location.state?.openCreate
  const initialCategory = location.state?.initialCategory ?? ""
  const shouldOpenInvites = !!location.state?.openInvites
  const queryParams = useMemo(() => new URLSearchParams(location.search || ""), [location.search])
  const requestedGroupId = normalizeGroupId(queryParams.get("groupId"))
  const [groupImpersonationScope, setGroupImpersonationScope] = useState(() => getGroupScopedImpersonation())
  const scopedGroupId = normalizeGroupId(groupImpersonationScope?.groupId)
  const isGroupScopedImpersonation = !!scopedGroupId
  const targetGroupId = isGroupScopedImpersonation ? scopedGroupId : requestedGroupId

  const [activeTab, setActiveTab] = useState(
    isGroupScopedImpersonation ? "details" : shouldOpenInvites ? "invites" : targetGroupId ? "details" : "groups"
  )
  const [groups, setGroups] = useState([])
  const [archivedGroups, setArchivedGroups] = useState([])
  const [invites, setInvites] = useState([])
  const [selectedGroupId, setSelectedGroupId] = useState(targetGroupId || null)
  const [openGroupTabIds, setOpenGroupTabIds] = useState(() => (targetGroupId ? [targetGroupId] : []))
  const [activeGroup, setActiveGroup] = useState(null)
  const [createError, setCreateError] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const [isLoadingGroups, setIsLoadingGroups] = useState(false)
  const [groupsError, setGroupsError] = useState("")
  const [isLoadingArchivedGroups, setIsLoadingArchivedGroups] = useState(false)
  const [archivedGroupsError, setArchivedGroupsError] = useState("")
  const [isLoadingGroupDetails, setIsLoadingGroupDetails] = useState(false)
  const [groupDetailsError, setGroupDetailsError] = useState("")
  const [payScore, setPayScore] = useState(null)
  const [isLoadingPayScore, setIsLoadingPayScore] = useState(false)
  const [payScoreError, setPayScoreError] = useState("")
  const [isLoadingInvites, setIsLoadingInvites] = useState(false)
  const [invitesError, setInvitesError] = useState("")
  const [isManualInvoicing, setIsManualInvoicing] = useState(false)
  const [isStartingChamaaCycle, setIsStartingChamaaCycle] = useState(false)
  const [isPausingChamaaLifecycle, setIsPausingChamaaLifecycle] = useState(false)
  const [isResumingChamaaLifecycle, setIsResumingChamaaLifecycle] = useState(false)
  const [isStoppingChamaaLifecycle, setIsStoppingChamaaLifecycle] = useState(false)
  const [isProcessingChamaaCycle, setIsProcessingChamaaCycle] = useState(false)
  const [isForceIssuingChamaaInvoice, setIsForceIssuingChamaaInvoice] = useState(false)
  const [isForceDisbursingChamaaCycle, setIsForceDisbursingChamaaCycle] = useState(false)
  const [isSimulatingChamaaAllPaid, setIsSimulatingChamaaAllPaid] = useState(false)
  const [isUpdatingChamaaMaxShares, setIsUpdatingChamaaMaxShares] = useState(false)
  const [isUpdatingChamaaMyShares, setIsUpdatingChamaaMyShares] = useState(false)
  const [isUpdatingNextCycleMember, setIsUpdatingNextCycleMember] = useState(false)
  const [isUpdatingNextCycleSettings, setIsUpdatingNextCycleSettings] = useState(false)
  const [isUpdatingWelfareSettings, setIsUpdatingWelfareSettings] = useState(false)
  const [isUpdatingJoinApproval, setIsUpdatingJoinApproval] = useState(false)
  const [isContributingNonRecurring, setIsContributingNonRecurring] = useState(false)
  const [isAddingWelfareChild, setIsAddingWelfareChild] = useState(false)
  const [isUpdatingGroupSimulator, setIsUpdatingGroupSimulator] = useState(false)
  const [isAdvancingGroupSimulator, setIsAdvancingGroupSimulator] = useState(false)
  const [isUpdatingAdminWorkflow, setIsUpdatingAdminWorkflow] = useState(false)
  const [isRenamingGroupId, setIsRenamingGroupId] = useState("")
  const [isUpdatingArchiveGroupId, setIsUpdatingArchiveGroupId] = useState("")
  const [pendingInvites, setPendingInvites] = useState([])
  const [pendingInvitesLoading, setPendingInvitesLoading] = useState(false)
  const [pendingInvitesError, setPendingInvitesError] = useState("")
  const [showCreateWizard, setShowCreateWizard] = useState(shouldOpenCreate)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [currentUserName, setCurrentUserName] = useState("")

  const selectedGroup = useMemo(() => {
    if (selectedGroupId) {
      const activeMatch =
        activeGroup && String(activeGroup.id) === String(selectedGroupId) ? activeGroup : null
      if (activeMatch) return activeMatch
      if (activeTab === "details") return null
      return (
        groups.find((g) => String(g.id) === String(selectedGroupId)) ||
        archivedGroups.find((g) => String(g.id) === String(selectedGroupId)) ||
        null
      )
    }
    return activeGroup || null
  }, [activeGroup, activeTab, archivedGroups, groups, selectedGroupId])

  const pendingInviteCount = useMemo(() => {
    if (!Array.isArray(invites)) return 0
    return invites.filter((invite) => String(invite?.status || "pending").toLowerCase() === "pending").length
  }, [invites])

  const ensureGroupTabOpen = useCallback((groupId) => {
    const normalized = normalizeGroupId(groupId)
    if (!normalized) return
    setOpenGroupTabIds((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]))
  }, [])

  const upsertGroupInList = useCallback((list, group) => {
    const normalized = normalizeGroupRecord(group)
    if (!normalized) return Array.isArray(list) ? list : []
    const listRows = Array.isArray(list) ? list : []
    const groupId = String(normalized.id)
    const next = listRows.filter((row) => String(row?.id || "") !== groupId)
    return [normalized, ...next]
  }, [])

  const syncGroupAcrossCollections = useCallback(
    (group) => {
      const normalized = normalizeGroupRecord(group)
      if (!normalized) return null
      const groupId = String(normalized.id)
      const archived = !!normalized.isArchivedForMe

      setGroups((prev) => {
        const listRows = Array.isArray(prev) ? prev : []
        if (archived) return listRows.filter((row) => String(row?.id || "") !== groupId)
        return upsertGroupInList(listRows, normalized)
      })

      setArchivedGroups((prev) => {
        const listRows = Array.isArray(prev) ? prev : []
        if (!archived) return listRows.filter((row) => String(row?.id || "") !== groupId)
        return upsertGroupInList(listRows, normalized)
      })

      return normalized
    },
    [upsertGroupInList]
  )

  const groupDetailTabs = useMemo(
    () =>
      openGroupTabIds.map((groupId) => {
        const activeMatch =
          activeGroup && String(activeGroup?.id || "") === String(groupId) ? activeGroup : null
        const fallbackMatch =
          groups.find((groupItem) => String(groupItem?.id || "") === String(groupId)) ||
          archivedGroups.find((groupItem) => String(groupItem?.id || "") === String(groupId)) ||
          null
        const matchedGroup = activeMatch || fallbackMatch
        const label = String(matchedGroup?.name || `Group ${String(groupId).slice(-4)}`).trim()
        return {
          id: groupId,
          label,
          shortLabel: toGroupTabShortName(label),
        }
      }),
    [activeGroup, archivedGroups, groups, openGroupTabIds]
  )

  useEffect(() => {
    const refreshScope = () => setGroupImpersonationScope(getGroupScopedImpersonation())
    window.addEventListener("storage", refreshScope)
    window.addEventListener("impersonationChanged", refreshScope)
    refreshScope()
    return () => {
      window.removeEventListener("storage", refreshScope)
      window.removeEventListener("impersonationChanged", refreshScope)
    }
  }, [])

  useEffect(() => {
    if (!isGroupScopedImpersonation) return
    if (activeTab !== "details") {
      setActiveTab("details")
    }
  }, [activeTab, isGroupScopedImpersonation])

  useEffect(() => {
    if (!targetGroupId) return
    ensureGroupTabOpen(targetGroupId)
  }, [ensureGroupTabOpen, targetGroupId])

  useEffect(() => {
    let mounted = true
    const loadPayScore = async () => {
      if (isGroupScopedImpersonation) {
        if (mounted) {
          setPayScore(null)
          setPayScoreError("")
          setIsLoadingPayScore(false)
        }
        return
      }

      setIsLoadingPayScore(true)
      setPayScoreError("")
      try {
        const res = await api.get("/payscore/me")
        if (mounted) setPayScore(res?.data || null)
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || "Failed to load PaySCORE."
        if (mounted) {
          setPayScore(null)
          setPayScoreError(message)
        }
      } finally {
        if (mounted) setIsLoadingPayScore(false)
      }
    }

    const loadActiveHome = async () => {
      if (isGroupScopedImpersonation) {
        if (mounted) {
          setGroups([])
          setArchivedGroups([])
          setInvites([])
          setGroupsError("")
          setArchivedGroupsError("")
          setInvitesError("")
          setIsLoadingGroups(false)
          setIsLoadingArchivedGroups(false)
          setIsLoadingInvites(false)
        }
        return
      }

      setIsLoadingGroups(true)
      setGroupsError("")
      try {
        const res = await api.get("/groups?view=home&archived=false")
        const list = Array.isArray(res?.data?.groups) ? res.data.groups : []
        if (mounted) setGroups(list)
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || "Failed to load groups."
        if (mounted) setGroupsError(message)
      } finally {
        if (mounted) setIsLoadingGroups(false)
      }
    }

    const loadArchivedHome = async () => {
      if (isGroupScopedImpersonation) {
        if (mounted) {
          setArchivedGroups([])
          setArchivedGroupsError("")
          setIsLoadingArchivedGroups(false)
        }
        return
      }

      setIsLoadingArchivedGroups(true)
      setArchivedGroupsError("")
      try {
        const res = await api.get("/groups?view=home&archived=true")
        const list = Array.isArray(res?.data?.groups) ? res.data.groups : []
        if (mounted) setArchivedGroups(list)
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || "Failed to load archived groups."
        if (mounted) setArchivedGroupsError(message)
      } finally {
        if (mounted) setIsLoadingArchivedGroups(false)
      }
    }

    const loadInvites = async () => {
      if (isGroupScopedImpersonation) return
      try {
        const res = await api.get("/groups/invites")
        const list = Array.isArray(res?.data?.invites) ? res.data.invites : []
        if (mounted) setInvites(list)
      } catch (err) {
        console.error("Failed to load invites indirectly", err)
      }
    }

    const loadScopedGroupDetails = async () => {
      if (!targetGroupId) return
      setSelectedGroupId(targetGroupId)
      ensureGroupTabOpen(targetGroupId)
      setActiveTab("details")
      setIsLoadingGroupDetails(true)
      setGroupDetailsError("")
      setActiveGroup(null)
      try {
        const res = await api.get(`/groups/${targetGroupId}/details`)
        const detailed = res?.data?.group
        if (!detailed?.id) {
          if (mounted) setGroupDetailsError("Group details are missing.")
          return
        }
        if (!mounted) return
        const synced = syncGroupAcrossCollections(detailed)
        setActiveGroup(synced || detailed)
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || "Failed to load group details."
        if (mounted) setGroupDetailsError(message)
      } finally {
        if (mounted) setIsLoadingGroupDetails(false)
      }
    }

    if (targetGroupId) {
      loadScopedGroupDetails()
    } else if (isGroupScopedImpersonation) {
      setGroupDetailsError("This impersonation session requires a group context.")
      setActiveTab("details")
    }

    loadActiveHome()
    loadArchivedHome()
    loadInvites()
    loadPayScore()

    return () => {
      mounted = false
    }
  }, [ensureGroupTabOpen, isGroupScopedImpersonation, syncGroupAcrossCollections, targetGroupId])

  useEffect(() => {
    let mounted = true
    const loadCurrentUser = async () => {
      try {
        const res = await api.get("/auth/me")
        if (!mounted) return
        const name = String(res?.data?.name || "").trim()
        setCurrentUserName(name)
      } catch {
        if (mounted) setCurrentUserName("")
      }
    }
    loadCurrentUser()
    return () => {
      mounted = false
    }
  }, [])

  const refreshGroupsHome = async () => {
    if (isGroupScopedImpersonation) {
      setGroups([])
      setGroupsError("")
      return []
    }
    setIsLoadingGroups(true)
    setGroupsError("")
    try {
      const res = await api.get("/groups?view=home&archived=false")
      const list = Array.isArray(res?.data?.groups) ? res.data.groups : []
      setGroups(list)
      return list
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to load groups."
      setGroupsError(message)
      return []
    } finally {
      setIsLoadingGroups(false)
    }
  }

  const refreshArchivedGroupsHome = async () => {
    if (isGroupScopedImpersonation) {
      setArchivedGroups([])
      setArchivedGroupsError("")
      return []
    }
    setIsLoadingArchivedGroups(true)
    setArchivedGroupsError("")
    try {
      const res = await api.get("/groups?view=home&archived=true")
      const list = Array.isArray(res?.data?.groups) ? res.data.groups : []
      setArchivedGroups(list)
      return list
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to load archived groups."
      setArchivedGroupsError(message)
      return []
    } finally {
      setIsLoadingArchivedGroups(false)
    }
  }

  const loadGroupDetails = async (groupId) => {
    const normalizedGroupId = normalizeGroupId(groupId)
    if (!normalizedGroupId) return
    ensureGroupTabOpen(normalizedGroupId)
    setIsLoadingGroupDetails(true)
    setGroupDetailsError("")
    setActiveGroup(null)
    try {
      const res = await api.get(`/groups/${normalizedGroupId}/details`)
      const detailed = res?.data?.group
      if (!detailed?.id) {
        setGroupDetailsError("Group details are missing.")
        return
      }
      const synced = syncGroupAcrossCollections(detailed)
      setActiveGroup(synced || detailed)
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to load group details."
      setGroupDetailsError(message)
    } finally {
      setIsLoadingGroupDetails(false)
    }
  }

  const refreshPayScore = async () => {
    if (isGroupScopedImpersonation) {
      setPayScore(null)
      setPayScoreError("")
      return { ok: true }
    }
    setIsLoadingPayScore(true)
    setPayScoreError("")
    try {
      const res = await api.get("/payscore/me")
      setPayScore(res?.data || null)
      return { ok: true }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to load PaySCORE."
      setPayScore(null)
      setPayScoreError(message)
      return { ok: false, message }
    } finally {
      setIsLoadingPayScore(false)
    }
  }

  const refreshInvites = async () => {
    if (isGroupScopedImpersonation) {
      setInvites([])
      setInvitesError("")
      return
    }
    setIsLoadingInvites(true)
    setInvitesError("")
    try {
      const res = await api.get("/groups/invites")
      const list = Array.isArray(res?.data?.invites) ? res.data.invites : []
      setInvites(list)
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to load invitations."
      setInvites([])
      setInvitesError(message)
    } finally {
      setIsLoadingInvites(false)
    }
  }

  useEffect(() => {
    if (isGroupScopedImpersonation) return
    if (activeTab !== "invites") return
    refreshInvites()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isGroupScopedImpersonation])

  const recoverCreatedGroupFromHome = async (payload, response) => {
    const data = response?.data || {}
    const responseGroupId = normalizeGroupId(data?.groupId || data?.createdGroupId || data?.id)
    const expectedName = String(payload?.groupName || "").trim().toLowerCase()
    const expectedCategory = String(payload?.eventTypeCategory || "").trim().toLowerCase()

    if (responseGroupId) {
      try {
        const detailsRes = await api.get(`/groups/${responseGroupId}/details`)
        const detailed = normalizeGroupRecord(detailsRes?.data?.group)
        if (detailed) return detailed
      } catch {
        // Fall back to home-list recovery below.
      }
    }

    const latestFirst = (a, b) => {
      const aTime = new Date(a?.createdAt || 0).getTime()
      const bTime = new Date(b?.createdAt || 0).getTime()
      return bTime - aTime
    }

    const list = await refreshGroupsHome()
    if (!Array.isArray(list) || !list.length) return null

    let recovered = null

    if (responseGroupId) {
      recovered = list.find((group) => normalizeGroupId(group?.id) === responseGroupId) || null
    }

    if (!recovered && expectedName) {
      recovered =
        list
          .filter((group) => String(group?.name || "").trim().toLowerCase() === expectedName)
          .filter((group) =>
            expectedCategory
              ? String(group?.category || "").trim().toLowerCase() === expectedCategory
              : true
          )
          .sort(latestFirst)[0] || null
    }

    if (!recovered && expectedCategory) {
      recovered =
        list
          .filter((group) => String(group?.category || "").trim().toLowerCase() === expectedCategory)
          .sort(latestFirst)[0] || null
    }

    const normalizedRecovered = normalizeGroupRecord(recovered)
    if (!normalizedRecovered) return null

    try {
      const detailsRes = await api.get(`/groups/${normalizedRecovered.id}/details`)
      const detailed = normalizeGroupRecord(detailsRes?.data?.group)
      return detailed || normalizedRecovered
    } catch {
      return normalizedRecovered
    }
  }

  const handleOpenCreate = () => {
    if (isGroupScopedImpersonation) return
    setCreateError("")
    setShowCreateWizard(true)
  }

  const handleCreateGroup = async (payload) => {
    setIsCreating(true)
    setCreateError("")
    try {
      const res = await api.post("/groups", payload)
      let created = resolveCreatedGroupFromResponse(res)
      if (!created) {
        created = await recoverCreatedGroupFromHome(payload, res)
      }
      if (!created) {
        setCreateError("Group created but response is missing. Please refresh.")
        return null
      }
      const synced = syncGroupAcrossCollections(created) || created
      setActiveGroup(synced)
      setSelectedGroupId(normalizeGroupId(synced.id))
      ensureGroupTabOpen(synced.id)
      setActiveTab("details")
      setShowCreateWizard(false)
      return synced
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to create group."
      setCreateError(message)
      return null
    } finally {
      setIsCreating(false)
    }
  }

  const handleSelectGroup = (groupId) => {
    const normalizedGroupId = normalizeGroupId(groupId)
    if (!normalizedGroupId) return
    if (isGroupScopedImpersonation && normalizedGroupId !== normalizeGroupId(targetGroupId)) return
    ensureGroupTabOpen(normalizedGroupId)
    setSelectedGroupId(normalizedGroupId)
    setActiveTab("details")
    loadGroupDetails(normalizedGroupId)
  }

  const handleAcceptInvite = async (inviteId) => {
    if (isGroupScopedImpersonation) return
    try {
      await api.post(`/groups/invites/${inviteId}/accept`)
      setInvites((prev) => prev.map((inv) => (inv.id === inviteId ? { ...inv, status: "accepted" } : inv)))
      await refreshInvites()
      // Refresh groups list so the accepted group appears.
      await Promise.all([refreshGroupsHome(), refreshArchivedGroupsHome()])
      // Also refresh pending invites count if looking at dashboard
      const invitesRes = await api.get("/groups/invites")
      setInvites(invitesRes?.data?.invites || [])
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to accept invitation."
      setCreateError(message)
    }
  }

  const handleRejectInvite = async (inviteId) => {
    if (isGroupScopedImpersonation) return
    try {
      await api.post(`/groups/invites/${inviteId}/reject`)
      setInvites((prev) => prev.map((inv) => (inv.id === inviteId ? { ...inv, status: "rejected" } : inv)))
      await refreshInvites()
      // Also refresh pending invites count if looking at dashboard
      const invitesRes = await api.get("/groups/invites")
      setInvites(invitesRes?.data?.invites || [])
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to reject invitation."
      setCreateError(message)
    }
  }

  const handleSetGroupArchived = async (groupId, archived) => {
    const normalizedGroupId = normalizeGroupId(groupId)
    if (!normalizedGroupId) {
      return { ok: false, message: "Group not found." }
    }
    setIsUpdatingArchiveGroupId(normalizedGroupId)
    setCreateError("")
    setGroupsError("")
    setArchivedGroupsError("")
    try {
      const res = await api.patch(`/groups/${normalizedGroupId}/archive`, { archived: !!archived })
      const updated = normalizeGroupRecord(res?.data?.group)
      if (!updated) {
        return { ok: false, message: "Archive state updated but response is missing group data." }
      }
      const synced = syncGroupAcrossCollections(updated) || updated
      if (normalizeGroupId(activeGroup?.id) === normalizedGroupId) {
        setActiveGroup(synced)
      }
      if (normalizeGroupId(selectedGroupId) === normalizedGroupId) {
        setActiveGroup(synced)
      }
      return {
        ok: true,
        group: synced,
        message:
          res?.data?.message ||
          (archived ? "Group archived for your personal view." : "Group reactivated."),
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to update archive state."
      setCreateError(message)
      setGroupsError(message)
      setArchivedGroupsError(message)
      return { ok: false, message }
    } finally {
      setIsUpdatingArchiveGroupId("")
    }
  }

  const handleArchiveGroup = async (groupId) => handleSetGroupArchived(groupId, true)

  const handleReactivateGroup = async (groupId) => handleSetGroupArchived(groupId, false)

  const handleManualInvoiceAll = async (amount) => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }

    setIsManualInvoicing(true)
    try {
      const res = await api.post(`/groups/${selectedGroup.id}/manual-invoice`, { amount })
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Invoice applied but response is missing updated group data." }
      }

      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Invoice sent to all members." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to invoice group members."
      return { ok: false, message }
    } finally {
      setIsManualInvoicing(false)
    }
  }

  const applyUpdatedGroupState = (updated) => {
    const synced = syncGroupAcrossCollections(updated)
    if (!synced?.id) return
    setActiveGroup(synced)
    setSelectedGroupId(normalizeGroupId(synced.id))
    ensureGroupTabOpen(synced.id)
    refreshPayScore()
  }

  const extractUpdatedGroupResponse = (response, successFallbackMessage) => {
    const updated = response?.data?.group
    if (!updated) {
      return { ok: false, message: "Updated group data is missing." }
    }
    applyUpdatedGroupState(updated)
    return { ok: true, message: response?.data?.message || successFallbackMessage }
  }

  const getApiErrorMessage = (error, fallbackMessage) =>
    error?.response?.data?.error || error?.message || fallbackMessage

  const runAdminWorkflowRequest = async ({
    validationMessage = "",
    request,
    successFallbackMessage,
    errorFallbackMessage,
  }) => {
    if (validationMessage) {
      return { ok: false, message: validationMessage }
    }
    setIsUpdatingAdminWorkflow(true)
    try {
      const response = await request()
      return extractUpdatedGroupResponse(response, successFallbackMessage)
    } catch (error) {
      return { ok: false, message: getApiErrorMessage(error, errorFallbackMessage) }
    } finally {
      setIsUpdatingAdminWorkflow(false)
    }
  }

  const handleStartChamaaCycle = async (payload) => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }

    setIsStartingChamaaCycle(true)
    try {
      const res = await api.post(`/groups/${selectedGroup.id}/cycles/start`, payload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Cycle started but updated group data is missing." }
      }

      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Cycle started successfully." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to start cycle."
      return { ok: false, message }
    } finally {
      setIsStartingChamaaCycle(false)
    }
  }

  const handleProcessChamaaCycle = async (cycleId, options = {}) => {
    if (!selectedGroup?.id || !cycleId) {
      return { ok: false, message: "Cycle not found." }
    }

    setIsProcessingChamaaCycle(true)
    try {
      const payload = {}
      if (options?.simulateNow) payload.simulateNow = options.simulateNow
      const res = await api.post(`/groups/${selectedGroup.id}/cycles/${cycleId}/process-due`, payload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Cycle processed but updated group data is missing." }
      }

      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Cycle due actions processed." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to process cycle."
      return { ok: false, message }
    } finally {
      setIsProcessingChamaaCycle(false)
    }
  }

  const handleForceIssueChamaaInvoice = async (cycleId, options = {}) => {
    if (!selectedGroup?.id || !cycleId) {
      return { ok: false, message: "Cycle not found." }
    }

    setIsForceIssuingChamaaInvoice(true)
    try {
      const payload = {}
      if (options?.simulateNow) payload.simulateNow = options.simulateNow
      const res = await api.post(`/groups/${selectedGroup.id}/cycles/${cycleId}/issue-invoice-now`, payload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Invoice issued but updated group data is missing." }
      }

      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Invoice issued for current round." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to issue invoice."
      return { ok: false, message }
    } finally {
      setIsForceIssuingChamaaInvoice(false)
    }
  }

  const handleForceDisburseChamaaCycle = async (cycleId, options = {}) => {
    if (!selectedGroup?.id || !cycleId) {
      return { ok: false, message: "Cycle not found." }
    }

    setIsForceDisbursingChamaaCycle(true)
    try {
      const payload = {}
      if (options?.simulateNow) payload.simulateNow = options.simulateNow
      const res = await api.post(`/groups/${selectedGroup.id}/cycles/${cycleId}/disburse-now`, payload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Payout disbursed but updated group data is missing." }
      }

      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Payout disbursed for current round." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to disburse payout."
      return { ok: false, message }
    } finally {
      setIsForceDisbursingChamaaCycle(false)
    }
  }

  const handleSimulateChamaaAllPaid = async (cycleId, options = {}) => {
    if (!selectedGroup?.id || !cycleId) {
      return { ok: false, message: "Cycle not found." }
    }

    setIsSimulatingChamaaAllPaid(true)
    try {
      const payload = {}
      if (options?.simulateNow) payload.simulateNow = options.simulateNow
      const res = await api.post(`/groups/${selectedGroup.id}/cycles/${cycleId}/simulate-all-paid`, payload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Simulation ran but updated group data is missing." }
      }

      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "All member payments simulated." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to simulate payments."
      return { ok: false, message }
    } finally {
      setIsSimulatingChamaaAllPaid(false)
    }
  }

  const handleUpdateChamaaMaxShares = async (maxSharesPerMember) => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }

    setIsUpdatingChamaaMaxShares(true)
    try {
      const res = await api.patch(`/groups/${selectedGroup.id}/enrollment/max-shares`, {
        maxSharesPerMember,
      })
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Enrollment max shares updated." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to update max shares."
      return { ok: false, message }
    } finally {
      setIsUpdatingChamaaMaxShares(false)
    }
  }

  const handleUpdateChamaaMyShares = async (desiredShares) => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }

    setIsUpdatingChamaaMyShares(true)
    try {
      const res = await api.patch(`/groups/${selectedGroup.id}/enrollment/my-shares`, {
        desiredShares,
      })
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Your desired shares were updated." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to update your shares."
      return { ok: false, message }
    } finally {
      setIsUpdatingChamaaMyShares(false)
    }
  }

  const handleUpdateNextCycleMember = async (memberId, payload = {}) => {
    if (!selectedGroup?.id || !memberId) {
      return { ok: false, message: "Group/member not found." }
    }
    setIsUpdatingNextCycleMember(true)
    try {
      const res = await api.patch(`/groups/${selectedGroup.id}/next-cycle/members/${memberId}`, payload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Next cycle preferences updated." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to update next cycle preferences."
      return { ok: false, message }
    } finally {
      setIsUpdatingNextCycleMember(false)
    }
  }

  const handleUpdateNextCycleSettings = async (payload = {}) => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }
    setIsUpdatingNextCycleSettings(true)
    try {
      const res = await api.patch(`/groups/${selectedGroup.id}/next-cycle/settings`, payload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Next cycle settings updated." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to update next cycle settings."
      return { ok: false, message }
    } finally {
      setIsUpdatingNextCycleSettings(false)
    }
  }

  const handleUpdateWelfareSettings = async (payload = {}) => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }
    setIsUpdatingWelfareSettings(true)
    try {
      const res = await api.patch(`/groups/${selectedGroup.id}/welfare/settings`, payload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Welfare settings updated." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to update Welfare settings."
      return { ok: false, message }
    } finally {
      setIsUpdatingWelfareSettings(false)
    }
  }

  const handleUpdateJoinApproval = async (required) => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }
    setIsUpdatingJoinApproval(true)
    try {
      const res = await api.patch(`/groups/${selectedGroup.id}/join-approval`, {
        required: !!required,
      })
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return {
        ok: true,
        message:
          res?.data?.message ||
          (required
            ? "New members must be approved before joining."
            : "Members can join immediately without approval."),
      }
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || "Failed to update joining approval."
      return { ok: false, message }
    } finally {
      setIsUpdatingJoinApproval(false)
    }
  }

  const handleContributeNonRecurring = async ({ memberId, amount }) => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }
    setIsContributingNonRecurring(true)
    try {
      const payload = {
        amount,
      }
      if (memberId) payload.memberId = memberId
      const res = await api.post(`/groups/${selectedGroup.id}/non-recurring/contributions`, payload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Contribution saved but updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return {
        ok: true,
        memberWalletBalance: Number(res?.data?.memberWalletBalance || 0),
        message:
          res?.data?.message ||
          (res?.data?.paidForAnotherMember
            ? "Contribution posted for the selected member."
            : "Contribution posted successfully."),
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to contribute."
      return { ok: false, message }
    } finally {
      setIsContributingNonRecurring(false)
    }
  }

  const handlePauseChamaaLifecycle = async () => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }

    setIsPausingChamaaLifecycle(true)
    try {
      const res = await api.post(`/groups/${selectedGroup.id}/chamaa/pause`)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Pause succeeded but updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Chamaa paused successfully." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to pause Chamaa."
      return { ok: false, message }
    } finally {
      setIsPausingChamaaLifecycle(false)
    }
  }

  const handleResumeChamaaLifecycle = async () => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }

    setIsResumingChamaaLifecycle(true)
    try {
      const res = await api.post(`/groups/${selectedGroup.id}/chamaa/resume`)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Resume succeeded but updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Chamaa resumed successfully." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to resume Chamaa."
      return { ok: false, message }
    } finally {
      setIsResumingChamaaLifecycle(false)
    }
  }

  const handleStopChamaaLifecycle = async () => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }

    setIsStoppingChamaaLifecycle(true)
    try {
      const res = await api.post(`/groups/${selectedGroup.id}/chamaa/stop`)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Stop succeeded but updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Chamaa stopped successfully." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to stop Chamaa."
      return { ok: false, message }
    } finally {
      setIsStoppingChamaaLifecycle(false)
    }
  }

  const handleAddWelfareChild = async (payload = {}) => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }
    setIsAddingWelfareChild(true)
    try {
      const res = await api.post(`/groups/${selectedGroup.id}/welfare/family-members/child`, payload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Child added to Welfare family members." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to add child."
      return { ok: false, message }
    } finally {
      setIsAddingWelfareChild(false)
    }
  }

  const handlePromoteGroupAdmin = async (memberId) => {
    return runAdminWorkflowRequest({
      validationMessage: !selectedGroup?.id || !memberId ? "Group/member not found." : "",
      request: () => api.post(`/groups/${selectedGroup.id}/admins/promote`, { memberId }),
      successFallbackMessage: "Admin role assigned.",
      errorFallbackMessage: "Failed to assign admin role.",
    })
  }

  const handleUpdateAdminTitle = async (memberId, adminTitle) => {
    return runAdminWorkflowRequest({
      validationMessage: !selectedGroup?.id || !memberId ? "Group/member not found." : "",
      request: () =>
        api.patch(`/groups/${selectedGroup.id}/admins/${memberId}/title`, {
          adminTitle,
        }),
      successFallbackMessage: "Admin title updated.",
      errorFallbackMessage: "Failed to update admin title.",
    })
  }

  const handleRequestAdminLeave = async () => {
    return runAdminWorkflowRequest({
      validationMessage: !selectedGroup?.id ? "No active group selected." : "",
      request: () => api.post(`/groups/${selectedGroup.id}/admin-requests/leave`),
      successFallbackMessage: "Leave request submitted.",
      errorFallbackMessage: "Failed to request admin leave.",
    })
  }

  const handleRequestAdminRemoval = async (memberId) => {
    return runAdminWorkflowRequest({
      validationMessage: !selectedGroup?.id || !memberId ? "Group/member not found." : "",
      request: () => api.post(`/groups/${selectedGroup.id}/admin-requests/remove`, { memberId }),
      successFallbackMessage: "Removal request submitted.",
      errorFallbackMessage: "Failed to create removal request.",
    })
  }

  const handleRequestSecretaryTransfer = async (memberId) => {
    return runAdminWorkflowRequest({
      validationMessage: !selectedGroup?.id || !memberId ? "Group/member not found." : "",
      request: () =>
        api.post(`/groups/${selectedGroup.id}/admin-requests/transfer-secretary`, {
          memberId,
        }),
      successFallbackMessage: "Secretary transfer request submitted.",
      errorFallbackMessage: "Failed to request secretary transfer.",
    })
  }

  const handleApproveAdminRequest = async (requestId) => {
    return runAdminWorkflowRequest({
      validationMessage: !selectedGroup?.id || !requestId ? "Group/request not found." : "",
      request: () => api.post(`/groups/${selectedGroup.id}/admin-requests/${requestId}/approve`),
      successFallbackMessage: "Approval saved.",
      errorFallbackMessage: "Failed to approve request.",
    })
  }

  const handleRejectAdminRequest = async (requestId) => {
    return runAdminWorkflowRequest({
      validationMessage: !selectedGroup?.id || !requestId ? "Group/request not found." : "",
      request: () => api.post(`/groups/${selectedGroup.id}/admin-requests/${requestId}/reject`),
      successFallbackMessage: "Request rejected.",
      errorFallbackMessage: "Failed to reject request.",
    })
  }

  const handleUpdateGroupSimulator = async (payload = {}) => {
    const targetGroupId = String(payload?.groupId || selectedGroup?.id || "")
    if (!targetGroupId) {
      return { ok: false, message: "No active group selected." }
    }
    const requestPayload = { ...payload }
    delete requestPayload.groupId

    setIsUpdatingGroupSimulator(true)
    try {
      const res = await api.patch(`/groups/${targetGroupId}/simulator`, requestPayload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Simulator settings updated." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to update simulator settings."
      return { ok: false, message }
    } finally {
      setIsUpdatingGroupSimulator(false)
    }
  }

  const handleAdvanceGroupSimulator = async (payload = {}) => {
    const targetGroupId = String(payload?.groupId || selectedGroup?.id || "")
    if (!targetGroupId) {
      return { ok: false, message: "No active group selected." }
    }
    const requestPayload = { ...payload }
    delete requestPayload.groupId

    setIsAdvancingGroupSimulator(true)
    try {
      const res = await api.post(`/groups/${targetGroupId}/simulator/advance`, requestPayload)
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Simulator advanced." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to run simulator action."
      return { ok: false, message }
    } finally {
      setIsAdvancingGroupSimulator(false)
    }
  }

  const handleRenameGroup = async (groupId, name) => {
    if (!groupId) return { ok: false, message: "Group not found." }
    const normalizedName = String(name || "").trim()
    if (!normalizedName) return { ok: false, message: "Group name is required." }

    setIsRenamingGroupId(String(groupId))
    try {
      const res = await api.patch(`/groups/${groupId}/name`, { name: normalizedName })
      const updated = res?.data?.group
      if (!updated) {
        return { ok: false, message: "Updated group data is missing." }
      }
      applyUpdatedGroupState(updated)
      return { ok: true, message: res?.data?.message || "Group name updated." }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to update group name."
      return { ok: false, message }
    } finally {
      setIsRenamingGroupId("")
    }
  }

  const handleImpersonateGroupMember = async (memberId) => {
    if (!selectedGroup?.id) {
      return { ok: false, message: "No active group selected." }
    }
    if (String(selectedGroup?.myRole || "").toLowerCase() !== "admin") {
      return { ok: false, message: "Only group admins can impersonate members." }
    }

    const targetUserId = String(memberId || "").trim()
    if (!targetUserId) {
      return { ok: false, message: "Member not found." }
    }
    if (String(selectedGroup?.myMemberId || "") === targetUserId) {
      return { ok: false, message: "You are already this member." }
    }

    try {
      const res = await api.post("/auth/impersonate-group-member", {
        groupId: selectedGroup.id,
        userId: targetUserId,
      })
      const token = String(res?.data?.token || "").trim()
      if (!token) {
        return { ok: false, message: "Failed to impersonate member: no token returned." }
      }

      const currentToken = localStorage.getItem("token") || ""
      if (currentToken) localStorage.setItem("impersonatorToken", currentToken)

      const me = Array.isArray(selectedGroup?.members)
        ? selectedGroup.members.find((member) => String(member?.id || "") === String(selectedGroup?.myMemberId || ""))
        : null

      localStorage.setItem("impersonationActive", "1")
      localStorage.setItem("impersonatorUserId", String(selectedGroup?.myMemberId || ""))
      localStorage.setItem("impersonatorName", String(me?.name || "Group Admin"))
      localStorage.setItem("impersonatedUserId", targetUserId)
      localStorage.setItem("impersonationReturnPath", `/app/groups?groupId=${encodeURIComponent(String(selectedGroup.id))}`)
      localStorage.setItem("token", token)
      window.dispatchEvent(new Event("impersonationChanged"))

      const destination = `/app/groups?groupId=${encodeURIComponent(String(selectedGroup.id))}&view=member`
      navigate(destination, { replace: true })
      window.location.reload()
      return { ok: true }
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || "Failed to impersonate this member."
      return { ok: false, message }
    }
  }

  const refreshPendingInvites = async (groupId = selectedGroup?.id) => {
    if (isGroupScopedImpersonation) {
      setPendingInvites([])
      setPendingInvitesError("")
      return
    }
    if (!groupId || selectedGroup?.myRole !== "admin") {
      setPendingInvites([])
      setPendingInvitesError("")
      return
    }

    setPendingInvitesLoading(true)
    setPendingInvitesError("")
    try {
      const res = await api.get(`/groups/${groupId}/invites/pending`)
      const list = Array.isArray(res?.data?.invites) ? res.data.invites : []
      setPendingInvites(list)
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || "Failed to load pending invites."
      setPendingInvites([])
      setPendingInvitesError(message)
    } finally {
      setPendingInvitesLoading(false)
    }
  }

  const handleSelectGroupDetailTab = (groupId) => {
    const normalizedGroupId = normalizeGroupId(groupId)
    if (!normalizedGroupId) return
    if (isGroupScopedImpersonation && normalizedGroupId !== normalizeGroupId(targetGroupId)) return
    ensureGroupTabOpen(normalizedGroupId)
    setSelectedGroupId(normalizedGroupId)
    setActiveTab("details")
    const isAlreadyActiveGroup = normalizeGroupId(activeGroup?.id) === normalizedGroupId
    if (!isAlreadyActiveGroup) {
      loadGroupDetails(normalizedGroupId)
    }
  }

  const handleCloseGroupDetailTab = (groupId) => {
    const normalizedGroupId = normalizeGroupId(groupId)
    if (!normalizedGroupId) return
    if (isGroupScopedImpersonation && normalizedGroupId === normalizeGroupId(targetGroupId)) return

    const currentTabs = Array.isArray(openGroupTabIds) ? openGroupTabIds : []
    if (!currentTabs.includes(normalizedGroupId)) return

    const closingIndex = currentTabs.indexOf(normalizedGroupId)
    const remainingTabs = currentTabs.filter((tabId) => tabId !== normalizedGroupId)
    setOpenGroupTabIds(remainingTabs)

    if (normalizeGroupId(selectedGroupId) !== normalizedGroupId) return

    if (remainingTabs.length === 0) {
      setSelectedGroupId(null)
      setActiveGroup(null)
      if (!isGroupScopedImpersonation) {
        setActiveTab("groups")
      }
      return
    }

    const nextIndex = closingIndex > 0 ? closingIndex - 1 : 0
    const nextGroupId = remainingTabs[nextIndex]
    setSelectedGroupId(nextGroupId)
    setActiveTab("details")
    loadGroupDetails(nextGroupId)
  }

  const handleNavbarTabChange = (tabId) => {
    if (isGroupScopedImpersonation && tabId !== "details") return

    if (tabId !== "details") {
      setActiveTab(tabId)
      return
    }

    setActiveTab("details")

    const activeGroupId = normalizeGroupId(selectedGroupId)
    if (activeGroupId) {
      ensureGroupTabOpen(activeGroupId)
      if (normalizeGroupId(activeGroup?.id) !== activeGroupId) {
        loadGroupDetails(activeGroupId)
      }
      return
    }

    const lastOpenedGroupId = openGroupTabIds.length > 0 ? openGroupTabIds[openGroupTabIds.length - 1] : ""
    if (lastOpenedGroupId) {
      setSelectedGroupId(lastOpenedGroupId)
      loadGroupDetails(lastOpenedGroupId)
    }
  }

  useEffect(() => {
    if (isGroupScopedImpersonation) {
      setPendingInvites([])
      setPendingInvitesError("")
      return
    }
    if (activeTab !== "details" || !selectedGroup?.id || selectedGroup?.myRole !== "admin") {
      setPendingInvites([])
      setPendingInvitesError("")
      return
    }
    refreshPendingInvites(selectedGroup.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedGroup?.id, selectedGroup?.myRole, isGroupScopedImpersonation])

  return (
    <>
      <GroupsLayout
        activeTab={activeTab}
        onTabChange={handleNavbarTabChange}
        detailGroupTabs={groupDetailTabs}
        activeDetailGroupId={normalizeGroupId(selectedGroupId)}
        onDetailGroupTabSelect={handleSelectGroupDetailTab}
        onDetailGroupTabClose={handleCloseGroupDetailTab}
        onOpenCreate={handleOpenCreate}
        invitesCount={pendingInviteCount}
        lockToDetailsOnly={isGroupScopedImpersonation}
        currentUserName={currentUserName}
      >
        {!isGroupScopedImpersonation && activeTab === "groups" && (
          <>
            <MonthlySummaryPanel
              endpoint="/groups/monthly-summary"
              title="Monthly Summary"
              subtitle="Your contributions & groups you run"
              icon="people-fill"
              accent="sky"
              emptyText="No group activity for this period."
            />
            {groupsError && (
              <div className="border border-red-300 bg-red-50 text-red-700 text-xs p-2 mb-3">
                {groupsError}
              </div>
            )}
            {isLoadingGroups && groups.length === 0 ? (
              <SkeletonList rows={3} />
            ) : (
            <GroupList
              groups={groups}
              viewMode="active"
              onSelectGroup={handleSelectGroup}
              onArchiveGroup={handleArchiveGroup}
              onRenameGroup={handleRenameGroup}
              isRenamingGroupId={isRenamingGroupId}
              isUpdatingArchiveGroupId={isUpdatingArchiveGroupId}
              payScore={payScore}
              payScoreLoading={isLoadingPayScore}
              payScoreError={payScoreError}
              onRefreshPayScore={refreshPayScore}
            />
            )}
          </>
        )}

        {!isGroupScopedImpersonation && activeTab === "archived" && (
          <>
            {archivedGroupsError && (
              <div className="border border-red-300 bg-red-50 text-red-700 text-xs p-2 mb-3">
                {archivedGroupsError}
              </div>
            )}
            {isLoadingArchivedGroups && archivedGroups.length === 0 ? (
              <SkeletonList rows={3} />
            ) : (
            <GroupList
              groups={archivedGroups}
              viewMode="archived"
              onSelectGroup={handleSelectGroup}
              onReactivateGroup={handleReactivateGroup}
              isUpdatingArchiveGroupId={isUpdatingArchiveGroupId}
              onRenameGroup={handleRenameGroup}
              isRenamingGroupId={isRenamingGroupId}
              showPayScore={false}
              showSearchPanel={false}
              registeredName={currentUserName}
            />
            )}
          </>
        )}

        {!isGroupScopedImpersonation && activeTab === "invites" && (
          <>
            {invitesError && (
              <div className="border border-red-300 bg-red-50 text-red-700 text-xs p-2 mb-3">
                {invitesError}
              </div>
            )}
            {isLoadingInvites && invites.length === 0 ? (
              <SkeletonList rows={2} />
            ) : (
              <GroupInvitesPanel invites={invites} onAccept={handleAcceptInvite} onReject={handleRejectInvite} />
            )}
          </>
        )}

        {activeTab === "details" && (
          <>
            {groupDetailsError && (
              <div className="border border-red-300 bg-red-50 text-red-700 text-xs p-2 mb-3">
                {groupDetailsError}
              </div>
            )}
            {isLoadingGroupDetails && !selectedGroup && (
              <div className="space-y-4">
                <SkeletonStatGrid count={3} />
                <SkeletonTable rows={5} />
              </div>
            )}
            <GroupDetails
              group={selectedGroup}
              createError={createError}
              onOpenInviteModal={() => setShowInviteModal(true)}
              onManualInvoiceAll={handleManualInvoiceAll}
              isManualInvoicing={isManualInvoicing}
              onStartChamaaCycle={handleStartChamaaCycle}
              onPauseChamaaLifecycle={handlePauseChamaaLifecycle}
              onResumeChamaaLifecycle={handleResumeChamaaLifecycle}
              onStopChamaaLifecycle={handleStopChamaaLifecycle}
              onProcessChamaaCycle={handleProcessChamaaCycle}
              onForceIssueChamaaInvoice={handleForceIssueChamaaInvoice}
              onForceDisburseChamaaCycle={handleForceDisburseChamaaCycle}
              onSimulateChamaaAllPaid={handleSimulateChamaaAllPaid}
              onUpdateChamaaMaxShares={handleUpdateChamaaMaxShares}
              onUpdateChamaaMyShares={handleUpdateChamaaMyShares}
              onUpdateNextCycleMember={handleUpdateNextCycleMember}
              onUpdateNextCycleSettings={handleUpdateNextCycleSettings}
              onUpdateWelfareSettings={handleUpdateWelfareSettings}
              onUpdateGroupJoinApproval={handleUpdateJoinApproval}
              onContributeNonRecurring={handleContributeNonRecurring}
              onAddWelfareChild={handleAddWelfareChild}
              onUpdateGroupSimulator={handleUpdateGroupSimulator}
              onAdvanceGroupSimulator={handleAdvanceGroupSimulator}
              onPromoteGroupAdmin={handlePromoteGroupAdmin}
              onUpdateAdminTitle={handleUpdateAdminTitle}
              onRequestAdminLeave={handleRequestAdminLeave}
              onRequestAdminRemoval={handleRequestAdminRemoval}
              onRequestSecretaryTransfer={handleRequestSecretaryTransfer}
              onApproveAdminRequest={handleApproveAdminRequest}
              onRejectAdminRequest={handleRejectAdminRequest}
              onGroupUpdated={applyUpdatedGroupState}
              onImpersonateMember={handleImpersonateGroupMember}
              isGroupScopedImpersonation={isGroupScopedImpersonation}
              isStartingChamaaCycle={isStartingChamaaCycle}
              isPausingChamaaLifecycle={isPausingChamaaLifecycle}
              isResumingChamaaLifecycle={isResumingChamaaLifecycle}
              isStoppingChamaaLifecycle={isStoppingChamaaLifecycle}
              isProcessingChamaaCycle={isProcessingChamaaCycle}
              isForceIssuingChamaaInvoice={isForceIssuingChamaaInvoice}
              isForceDisbursingChamaaCycle={isForceDisbursingChamaaCycle}
              isSimulatingChamaaAllPaid={isSimulatingChamaaAllPaid}
              isUpdatingChamaaMaxShares={isUpdatingChamaaMaxShares}
              isUpdatingChamaaMyShares={isUpdatingChamaaMyShares}
              isUpdatingNextCycleMember={isUpdatingNextCycleMember}
              isUpdatingNextCycleSettings={isUpdatingNextCycleSettings}
              isUpdatingWelfareSettings={isUpdatingWelfareSettings}
              isUpdatingGroupJoinApproval={isUpdatingJoinApproval}
              isContributingNonRecurring={isContributingNonRecurring}
              isAddingWelfareChild={isAddingWelfareChild}
              isUpdatingGroupSimulator={isUpdatingGroupSimulator}
              isAdvancingGroupSimulator={isAdvancingGroupSimulator}
              isUpdatingAdminWorkflow={isUpdatingAdminWorkflow}
              pendingInvites={pendingInvites}
              pendingInvitesLoading={pendingInvitesLoading}
              pendingInvitesError={pendingInvitesError}
              isArchivedReadOnly={!!selectedGroup?.isArchivedForMe}
              onReactivateArchivedGroup={handleReactivateGroup}
              isReactivatingArchivedGroup={
                normalizeGroupId(isUpdatingArchiveGroupId) !== "" &&
                normalizeGroupId(isUpdatingArchiveGroupId) === normalizeGroupId(selectedGroup?.id)
              }
            />
          </>
        )}
      </GroupsLayout>

      {!isGroupScopedImpersonation && showCreateWizard && (
        <GroupCreationWizard
          onClose={() => setShowCreateWizard(false)}
          onCreate={handleCreateGroup}
          isSubmitting={isCreating}
          errorMessage={createError}
          initialCategory={initialCategory}
        />
      )}

      {!isGroupScopedImpersonation && showInviteModal && (
        <GroupInviteModal
          open={showInviteModal}
          group={selectedGroup}
          onClose={() => setShowInviteModal(false)}
          onSent={() => refreshPendingInvites(selectedGroup?.id)}
        />
      )}
    </>
  )
}

export default GroupsModule
